import { Button } from "@shared-ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import { RetireVariantDialog } from "@/features/catalog/components/retire-variant-dialog";
import {
	type DeclaredAxis,
	VariantDialog,
	type VariantDialogMode,
} from "@/features/catalog/components/variant-dialog";
import type { AdminVariant } from "@/features/catalog/services/catalog-api.service";

/**
 * The Variants tab.
 *
 * The tab has been disabled since plan 008 shipped it, so variants have been
 * creatable only through the API. Nothing about a variant -- its SKU, its
 * finish, whether it still exists -- was reachable by the people who catalogue.
 *
 * Owns its own mutations, the way `product-media.tsx` does, so the editor's save
 * cycle and this tab's writes do not have to agree about anything. The list is
 * whatever the product detail returned; a write invalidates that query rather
 * than patching a local copy, because the server normalises what it stores and
 * a local copy would drift from it.
 */

/** The axis columns a variant carries, as "Finish: Matte" pairs. */
function axisSummary(variant: AdminVariant): string {
	const pairs: string[] = [];
	if (variant.variantAxis1Name && variant.variantAxis1Value) {
		pairs.push(`${variant.variantAxis1Name}: ${variant.variantAxis1Value}`);
	}
	if (variant.variantAxis2Name && variant.variantAxis2Value) {
		pairs.push(`${variant.variantAxis2Name}: ${variant.variantAxis2Value}`);
	}
	return pairs.join(" · ");
}

interface VariantListProps {
	productId: string;
	variants: AdminVariant[];
	/** The category's declared axes; empty when its template declares none. */
	axes: DeclaredAxis[];
	canWrite: boolean;
}

export function VariantList({
	productId,
	variants,
	axes,
	canWrite,
}: VariantListProps) {
	const queryClient = useQueryClient();
	const [dialog, setDialog] = useState<VariantDialogMode | null>(null);
	const [retiring, setRetiring] = useState<AdminVariant | null>(null);

	const refresh = async () => {
		await queryClient.invalidateQueries({
			queryKey: ["admin-catalog-product", productId],
		});
	};

	/*
	 * A CATEGORY WITH NO DECLARED AXES GETS AN EXPLANATION, not an empty form.
	 *
	 * Without axes the dialog is a SKU box and nothing else, which looks broken
	 * rather than deliberate -- and the fix is not on this screen: axes are
	 * declared on the category's spec template. Say where to go.
	 *
	 * A BANNER, NOT A REPLACEMENT FOR THE TABLE. It used to be checked before
	 * `variants.length` and rendered instead of the list, so a category whose
	 * last axis had been removed hid every variant the product already had --
	 * still live, still sellable, still returned by every public read, and no
	 * longer reachable from the only screen that can retire one. Removing an
	 * axis is exactly what the impact preview warns about; the tab that lets an
	 * operator act on that warning must not be the thing that disappears.
	 */
	const hasAxes = axes.length > 0;

	return (
		<div className="grid gap-4">
			<div className="flex items-start justify-between gap-4">
				<p className="text-sm text-muted-foreground">
					Variants are the same product in a different finish, size or colour.
					Each carries its own SKU. A variant&apos;s price is recorded on the
					Pricing tab.
				</p>
				{canWrite && hasAxes ? (
					<Button onClick={() => setDialog({ kind: "create" })}>
						Add a variant
					</Button>
				) : null}
			</div>

			{!hasAxes ? (
				<div className="grid gap-2 rounded-2xl border border-dashed p-6 text-sm">
					<p className="font-medium">
						This category does not define any variant axes.
					</p>
					<p className="text-muted-foreground">
						A variant has to vary along something — a finish, a size, a colour.
						Declare the axes on the category's specification template first, and
						they will appear here as fields.
						{variants.length > 0
							? " The variants below are still live and can still be retired; they just have nothing describing them."
							: ""}
					</p>
				</div>
			) : null}

			{variants.length === 0 ? (
				hasAxes ? (
					/*
					 * ZERO VARIANTS IS A REAL STATE, not a loading one. Variants are
					 * optional and a product without them is sold as itself -- so this
					 * says so rather than rendering an empty table that reads as a
					 * failure.
					 *
					 * The dev seed never produces it: all 11 seeded products carry
					 * variants, 29 in total. That is why this state has no seeded fixture
					 * and is driven from a hand-built one in the tests.
					 */
					<EmptyState
						title="No variants"
						hint="This product is sold as itself. Add a variant when the same product comes in more than one finish or size."
					/>
				) : null
			) : (
				<DataTable
					rows={variants}
					rowKey={(variant) => variant.id}
					columns={[
						{
							header: "SKU",
							cell: (variant) => (
								<span className="font-medium">{variant.sku}</span>
							),
						},
						{
							header: "Axes",
							cell: (variant) =>
								axisSummary(variant) || (
									<span className="text-muted-foreground">—</span>
								),
						},
						{
							header: "Brand SKU",
							cell: (variant) =>
								variant.brandSku ?? (
									<span className="text-muted-foreground">—</span>
								),
						},
						{
							header: "",
							cell: (variant) =>
								canWrite ? (
									<div className="flex justify-end gap-2">
										<Button
											variant="outline"
											size="sm"
											aria-label={`Edit ${variant.sku}`}
											onClick={() => setDialog({ kind: "edit", variant })}
										>
											Edit
										</Button>
										<Button
											variant="ghost"
											size="sm"
											aria-label={`Retire ${variant.sku}`}
											onClick={() => setRetiring(variant)}
										>
											Retire
										</Button>
									</div>
								) : null,
						},
					]}
				/>
			)}

			{dialog ? (
				<VariantDialog
					// Keyed so a retarget -- edit one variant, close, edit another --
					// cannot carry the previous variant's draft or error banner.
					key={dialog.kind === "edit" ? dialog.variant.id : "create"}
					productId={productId}
					mode={dialog}
					axes={axes}
					onClose={() => setDialog(null)}
					onSaved={refresh}
				/>
			) : null}

			{retiring ? (
				<RetireVariantDialog
					key={retiring.id}
					variant={retiring}
					onClose={() => setRetiring(null)}
					onRetired={refresh}
				/>
			) : null}
		</div>
	);
}
